import axios, {AxiosRequestConfig, AxiosError} from 'axios';
import axiosRetry from 'axios-retry';
import {BaseQueryFn} from '@reduxjs/toolkit/query';

const axiosInstance = axios.create();

const READ_ONLY_METHODS = new Set(['get', 'head', 'options']);

axiosRetry(axiosInstance, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error: AxiosError) => {
    const method = error.config?.method?.toLowerCase() ?? '';
    const url = error.config?.url ?? '';

    // Read-only methods are inherently safe to retry on network errors or 5xx.
    if (READ_ONLY_METHODS.has(method)) {
      return axiosRetry.isNetworkOrIdempotentRequestError(error);
    }

    // AI generation endpoints don't write to our DB, so retrying on a transient
    // network error is safe. Trade-off: a client-side timeout after the server
    // already completed generation may duplicate the upstream LLM call —
    // accepted for the UX win of recovering from transient failures.
    if (url.includes('/apiv2/ai/')) {
      return axiosRetry.isNetworkError(error);
    }

    // All other POST/PATCH/PUT/DELETE: do NOT retry without an idempotency key.
    return false;
  },
});

export const axiosBaseQuery
  = ({baseUrl}: { baseUrl?: string } = {}): BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
      headers?: AxiosRequestConfig['headers'];
    },
    unknown,
    unknown
  > =>
    async ({url, method, data, params, headers}) => {
      try {
        if (!url) {
          return {
            error: {
              status: 400,
              data: 'URL is required',
            },
          };
        }

        const result = await axiosInstance.request({
          url: (baseUrl ?? '') + url,
          method,
          data,
          params,
          headers: {...headers, 'X-Requested-With': 'XMLHttpRequest'},
          withCredentials: true,
        });

        return {data: result.data};
      } catch (axiosError) {
        const err = axiosError as AxiosError;

        return {
          error: {
            status: err?.response?.status,
            data: err?.response?.data || err?.message,
          },
        };
      }
    };